import { useCallback, useMemo, useSyncExternalStore } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Bilingual service app: English and Arabic, with RTL.
//
// Shared by BOTH surfaces — the Kitchen Display and the Service Manager grid —
// which is why it sits in lib/ rather than under features/kds where it started.
//
// The whole app is bilingual because the kitchens are. The item names were
// already coming through in both scripts (Inv_Items.Name / Name_AR, picked by
// `pickName`); this covers the chrome around them — the buttons a cook presses
// and the words that tell them what a card is doing.
//
// LANGUAGE RESOLUTION, most specific first:
//   1. This screen's own choice, in localStorage. A branch can run an Arabic
//      pass and an English grill if that is who is standing there, and the
//      choice must survive a reboot.
//   2. kds_settings.language for the branch, set by the admin.
//   3. Arabic, matching the rest of the estate's default.
//
// DIRECTION follows the language and is applied to each surface's own root
// rather than to <html>, so the two screens stay independent of each other and
// of whatever else the page holds. Both stylesheets are written in logical
// properties (border-inline-start, margin-inline-start, text-align: start/end),
// so nothing else has to change for the mirror to be correct.
//
// The store is a two-line external store rather than zustand so that `useT`
// can be called from anywhere, including outside a provider, and so a language
// change re-renders exactly the components that read it.
// ─────────────────────────────────────────────────────────────────────────────

export type Lang = 'en' | 'ar';

const STORAGE_KEY = 'matix_kds_lang';

const DICT: Record<string, { en: string; ar: string }> = {
  kitchen: { en: 'Kitchen', ar: 'المطبخ' },
  station: { en: 'Station', ar: 'المحطة' },
  expo: { en: 'Expo', ar: 'التسليم' },
  all: { en: 'All', ar: 'الكل' },
  allDay: { en: 'All-day', ar: 'الإجمالي' },
  recall: { en: 'Recall', ar: 'استرجاع' },

  open: { en: 'Open', ar: 'مفتوح' },
  late: { en: 'Late', ar: 'متأخر' },
  live: { en: 'Live', ar: 'مباشر' },
  polling: { en: 'Polling', ar: 'تحديث دوري' },
  reconnecting: { en: 'Reconnecting', ar: 'إعادة الاتصال' },
  connecting: { en: 'Connecting', ar: 'جارٍ الاتصال' },

  refresh: { en: 'Refresh', ar: 'تحديث' },
  fullScreen: { en: 'Full screen', ar: 'ملء الشاشة' },
  signOut: { en: 'Sign out', ar: 'تسجيل الخروج' },
  serviceManager: { en: 'Service Manager', ar: 'إدارة الخدمة' },
  kds: { en: 'Kitchen Display', ar: 'شاشة المطبخ' },

  start: { en: 'Start', ar: 'ابدأ' },
  bump: { en: 'Bump', ar: 'تم' },
  serve: { en: 'Serve', ar: 'تسليم' },
  served: { en: 'Served', ar: 'تم التسليم' },
  fire: { en: 'Fire', ar: 'إطلاق' },
  rush: { en: 'Mark rush', ar: 'عاجل' },
  clearRush: { en: 'Clear rush', ar: 'إلغاء العاجل' },
  recalled: { en: 'Recalled', ar: 'مُسترجع' },

  // ── Serve a delivery order ───────────────────────────────────────────────
  handOver: { en: 'Hand over for delivery', ar: 'تسليم للتوصيل' },
  driver: { en: 'Driver', ar: 'السائق' },
  driverOptional: { en: 'Driver (optional)', ar: 'السائق (اختياري)' },
  selectDriver: { en: 'Select driver', ar: 'اختر السائق' },
  noDriver: { en: 'Without a driver', ar: 'بدون سائق' },
  serveNoDriver: { en: 'Hand over — no driver', ar: 'تسليم — بدون سائق' },
  serveWithDriver: { en: 'Hand over with driver', ar: 'تسليم مع السائق' },
  loadingDrivers: { en: 'Loading drivers…', ar: 'جارٍ تحميل السائقين…' },
  driversFailed: { en: 'Couldn’t load drivers.', ar: 'تعذّر تحميل السائقين.' },
  noDriversConfigured: {
    en: 'No drivers set up. You can still hand the order over.',
    ar: 'لا يوجد سائقون مُعرّفون. لا يزال بإمكانك تسليم الطلب.',
  },
  retry: { en: 'Retry', ar: 'إعادة المحاولة' },
  cancel: { en: 'Cancel', ar: 'إلغاء' },
  searchDriver: { en: 'Search…', ar: 'بحث…' },
  noMatches: { en: 'No matches', ar: 'لا توجد نتائج' },
  deliveryNoMoney: {
    en: 'No money is taken here — the till settles it when the driver returns.',
    ar: 'لا يتم تحصيل أي مبلغ هنا — تتم التسوية على الكاشير عند عودة السائق.',
  },

  held: { en: 'HELD', ar: 'محجوز' },
  course: { en: 'course', ar: 'الطبق' },
  cancelled: { en: 'CANCELLED — do not make', ar: 'ملغي — لا تُحضّر' },

  loadingBoard: { en: 'Loading the board…', ar: 'جارٍ تحميل الشاشة…' },
  loadingSession: { en: 'Loading session…', ar: 'جارٍ تحميل الجلسة…' },
  nothingOnBoard: { en: 'Nothing on the board.', ar: 'لا توجد طلبات حالياً.' },
  nothingBumped: { en: 'Nothing bumped recently.', ar: 'لا يوجد ما تم إنهاؤه مؤخراً.' },

  item: { en: 'Item', ar: 'الصنف' },
  waiting: { en: 'Waiting', ar: 'بالانتظار' },
  cooking: { en: 'Cooking', ar: 'قيد التحضير' },
  ready: { en: 'Ready', ar: 'جاهز' },
  total: { en: 'Total', ar: 'الإجمالي' },
  unassigned: { en: 'Unassigned', ar: 'غير مخصص' },

  dineIn: { en: 'Dine-in', ar: 'صالة' },
  walkIn: { en: 'Walk-in', ar: 'سفري' },
  pickup: { en: 'Pickup', ar: 'استلام' },
  delivery: { en: 'Delivery', ar: 'توصيل' },
  membership: { en: 'Membership', ar: 'اشتراك' },

  srcQr: { en: 'QR', ar: 'QR' },
  srcOnline: { en: 'Online', ar: 'أونلاين' },
  srcCallCenter: { en: 'Call center', ar: 'مركز الاتصال' },
  srcOffline: { en: 'Offline', ar: 'دون اتصال' },
  srcBooking: { en: 'Booking', ar: 'حجز' },

  // ── Service Manager ──────────────────────────────────────────────────────
  branch: { en: 'Branch', ar: 'الفرع' },
  orderType: { en: 'Order Type', ar: 'نوع الطلب' },
  date: { en: 'Date', ar: 'التاريخ' },
  categories: { en: 'Categories', ar: 'الأقسام' },
  allBranches: { en: 'All branches', ar: 'كل الفروع' },
  allTypes: { en: 'All types', ar: 'كل الأنواع' },
  allCategories: { en: 'All categories', ar: 'كل الأقسام' },
  selectCategories: { en: 'All categories', ar: 'كل الأقسام' },
  selected: { en: 'selected', ar: 'محدد' },
  selectAll: { en: 'Select all', ar: 'تحديد الكل' },
  clear: { en: 'Clear', ar: 'مسح' },
  today: { en: 'Today', ar: 'اليوم' },
  dateBasis: { en: 'Date applies to', ar: 'التاريخ يخص' },
  orderDate: { en: 'Order date', ar: 'تاريخ الطلب' },
  deliveryDate: { en: 'Delivery date', ar: 'تاريخ التسليم' },
  filters: { en: 'Filters', ar: 'التصفية' },
  showFilters: { en: 'Show filters', ar: 'إظهار التصفية' },
  hideFilters: { en: 'Hide filters', ar: 'إخفاء التصفية' },
  resetFilters: { en: 'Reset', ar: 'إعادة تعيين' },
  search: { en: 'Search order, table, item…', ar: 'بحث برقم الطلب أو الطاولة أو الصنف…' },
  lateOnly: { en: 'Late only', ar: 'المتأخر فقط' },

  details: { en: 'DETAILS', ar: 'التفاصيل' },
  items: { en: 'ITEMS', ar: 'الأصناف' },
  options: { en: 'OPTIONS', ar: 'الإضافات' },
  waitingTime: { en: 'Waiting time', ar: 'مدة الانتظار' },
  days: { en: 'Day(s)', ar: 'يوم' },
  hours: { en: 'Hour(s)', ar: 'ساعة' },
  minutes: { en: 'Min', ar: 'دقيقة' },
  seconds: { en: 'Sec', ar: 'ثانية' },
  print: { en: 'PRINT', ar: 'طباعة' },
  kitchenPrint: { en: 'KITCHEN', ar: 'المطبخ' },
  printReceipt: { en: 'Print customer receipt', ar: 'طباعة فاتورة العميل' },
  printKitchen: { en: 'Print kitchen ticket', ar: 'طباعة طلب المطبخ' },
  markReady: { en: 'Ready', ar: 'جاهز' },
  allReady: { en: 'All ready', ar: 'الكل جاهز' },
  confirmReady: { en: 'Tap again to confirm', ar: 'اضغط مرة أخرى للتأكيد' },
  ofReady: { en: 'ready', ar: 'جاهز' },
  customer: { en: 'Customer', ar: 'العميل' },
  table: { en: 'Table', ar: 'الطاولة' },
  orderNo: { en: 'Order no.', ar: 'رقم الطلب' },
  placed: { en: 'Placed', ar: 'وقت الطلب' },
  noOrders: { en: 'No orders match these filters.', ar: 'لا توجد طلبات مطابقة.' },
  loadingQueue: { en: 'Loading the queue…', ar: 'جارٍ تحميل الطلبات…' },
  noLines: { en: 'No items on this order.', ar: 'لا توجد أصناف في هذا الطلب.' },
  sent: { en: 'Sent to the printer', ar: 'أُرسل إلى الطابعة' },
  printFailed: { en: 'Could not print', ar: 'تعذّرت الطباعة' },
  day: { en: 'Day', ar: 'اليوم' },
  orders: { en: 'Orders', ar: 'الطلبات' },
  hidden: { en: 'hidden by filters', ar: 'مخفي بالتصفية' },
  lightMode: { en: 'Light mode', ar: 'الوضع الفاتح' },
  darkMode: { en: 'Dark mode', ar: 'الوضع الداكن' },
};

export type TKey = keyof typeof DICT;

// ── external store ──────────────────────────────────────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

/** Language the admin configured for this branch; the fallback for #1 above. */
let branchLang: Lang = 'ar';
let override: Lang | null = readStored();

function readStored(): Lang | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'en' || raw === 'ar' ? raw : null;
  } catch {
    return null;
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Lang {
  return override ?? branchLang;
}

/**
 * The language right now, outside React.
 *
 * The printing path needs it and is not a component — the same reason the
 * cashier has `currentPrintLanguage`. Reads the same snapshot the hooks do,
 * so a slip never comes out in a different language from the screen.
 */
export function currentLanguage(): Lang {
  return snapshot();
}

/** Called once from bootstrap with kds_settings.language. */
export function setBranchLanguage(language: string | undefined): void {
  const next: Lang = (language ?? 'ar').toLowerCase().startsWith('en') ? 'en' : 'ar';
  if (next === branchLang) return;
  branchLang = next;
  if (!override) emit();
}

/** The per-screen toggle. Passing null hands control back to the branch setting. */
export function setLanguage(lang: Lang | null): void {
  override = lang;
  try {
    if (lang) localStorage.setItem(STORAGE_KEY, lang);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / storage full. The choice still applies for this session.
  }
  emit();
}

// ── hooks ───────────────────────────────────────────────────────────────────

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * The translator.
 *
 * Memoised on the language, and the language only. This matters: an unmemoised
 * `t` is a new function identity on every render, which defeats every
 * React.memo below it — and on this screen those memos are what keep a
 * forty-card board from re-reconciling once a second.
 */
export function useT(): (key: TKey) => string {
  const lang = useLang();
  return useCallback((key: TKey) => DICT[key]?.[lang] ?? String(key), [lang]);
}

export function useDir(): 'rtl' | 'ltr' {
  return useLang() === 'ar' ? 'rtl' : 'ltr';
}

/** Language + direction + translator in one memoised object, for components
 *  that need all three and would otherwise call three hooks and re-render on
 *  each. */
export function useI18n(): { lang: Lang; dir: 'rtl' | 'ltr'; t: (key: TKey) => string } {
  const lang = useLang();
  const t = useT();
  return useMemo(() => ({ lang, dir: lang === 'ar' ? 'rtl' : 'ltr', t }), [lang, t]);
}
